import { useRef, useState, type ReactNode } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import checkmarkIcon from '../../../assets/missions/checkmark.png';
import giftIconJson from '../../../assets/missions/gift-icon-sprite.json';
import giftIconSprite from '../../../assets/missions/gift-icon-sprite.png';
import { useSpriteSheet, type SpriteSheetAsset } from '../../../hooks/useSpriteSheet';
import { analytics } from '../../../services/analytics';
import type { BundleItem } from '../../../stores/rewards/types';
import { formatValueWithK } from '../../../utils/helpers';
import { BundleItemsRenderer } from '../../BundleItemsRenderer/BundleItemsRenderer';
import { CoinWithValue } from '../../CoinWithValue/CoinWithValue';
import { SpriteAnimation } from '../../EventTriggerDialog/road/components/SpriteAnimation/SpriteAnimation';
import { DynamicTooltip, type ArrowPosition, type TooltipPosition } from '../../Tooltip/DynamicTooltip';
import { MISSIONS_TEST_ID_PREFIX } from '../constants';

import classes from './GiftTooltip.module.scss';

const GRAND_PRIZE_GIFT_ICON_SPRITE: SpriteSheetAsset = {
  imageSrc: giftIconSprite,
  dataSrc: JSON.stringify(giftIconJson),
  frameCount: 30,
  animationDuration: 600,
};

type RewardEventName = 'open_streak_reward' | 'close_streak_reward' | 'open_milestone_reward';

export type GiftTooltipProps = {
  scCoin: number;
  gcCoin: number;
  bundleItems?: BundleItem[] | null;
  playAnimation?: boolean;
  claimed?: boolean;
  icon?: ReactNode;
  position?: TooltipPosition;
  arrowPosition?: ArrowPosition;
  analyticsEventName?: RewardEventName;
  closeAnalyticsEventName?: RewardEventName;
};

export const GiftTooltip = ({
  scCoin,
  gcCoin,
  bundleItems,
  playAnimation,
  claimed,
  icon,
  position = 'bottom',
  arrowPosition = 'top',
  analyticsEventName = 'open_milestone_reward',
  closeAnalyticsEventName,
}: GiftTooltipProps) => {
  const { t } = useTranslation();
  // Div ref so it works for both the SpriteAnimation wrapper and the image wrapper (tooltip target).
  const iconRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const giftSpriteState = useSpriteSheet(GRAND_PRIZE_GIFT_ICON_SPRITE);

  const handleClick = () => {
    const eventName = isOpen ? closeAnalyticsEventName : analyticsEventName;
    if (eventName) {
      const rewards: string[] = [
        ...(scCoin > 0 ? [`${scCoin.toLocaleString()}SC`] : []),
        ...(gcCoin > 0 ? [formatValueWithK(gcCoin, t)] : []),
        ...(bundleItems?.map((item) => item.bundleType) ?? []),
      ];
      void analytics.sendEvent('daily_missions', {
        event_category: 'daily_missions',
        event_name: eventName,
        event_action: 'click',
        event_type: 'reward',
        event_value: rewards,
        page_url: window.location.pathname,
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      {icon ? (
        <div
          ref={iconRef}
          aria-label={t('missions.reward', 'Reward')}
          className={classes.customIcon}
          data-testid={`${MISSIONS_TEST_ID_PREFIX}-progress-bar-gift-icon`}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
        >
          {icon}
        </div>
      ) : (
        <SpriteAnimation
          className={classes.grandPrizeGiftIcon}
          dataTestId={`${MISSIONS_TEST_ID_PREFIX}-progress-bar-gift-icon`}
          frameInterval={
            giftSpriteState.frameCount ? giftSpriteState.animationDuration / giftSpriteState.frameCount : undefined
          }
          idleFrame={'first'}
          isPlaying={!!playAnimation}
          refProp={iconRef}
          scale={0.8}
          sprite={giftSpriteState}
          onClick={handleClick}
        />
      )}
      <DynamicTooltip
        arrowAlign="dynamic"
        arrowPosition={arrowPosition}
        isOpen={isOpen}
        position={position}
        targetRef={iconRef}
        onClose={() => setIsOpen(false)}
      >
        <div className={classes.giftTooltip} data-testid={`${MISSIONS_TEST_ID_PREFIX}-progress-bar-gift-tooltip`}>
          {bundleItems && bundleItems.length > 0 && (
            <BundleItemsRenderer
              hidePlus
              bundleItems={bundleItems}
              className={classes.dailyMissionsGiftTooltipBundleItems}
              direction={'vertical-v2'}
              frbWidgetWidth={35}
              metagameIconClassName={classes.metagameIcon}
              metaGameWidgetWidth={34}
              ripsPackImageClassName={classes.ripsPackImage}
              snappyGiftWidgetWidth={30}
            />
          )}
          {scCoin > 0 && (
            <div className={classes.reward}>
              <CoinWithValue
                isPile
                className={classNames(classes.tooltipReward, { [classes.rewardDimmed]: claimed })}
                coinSide="top"
                coinType="SC"
                coinWidth={40}
                dataTestId={`${MISSIONS_TEST_ID_PREFIX}-gift-tooltip-reward-SC`}
                valueClassName={classes.coinValue}
                valueDataTestId={`${MISSIONS_TEST_ID_PREFIX}-gift-tooltip-reward-SC-value`}
              >
                <span className={classes.coinValue}>{scCoin.toLocaleString()}</span>
              </CoinWithValue>
              {claimed && (
                <img
                  alt=""
                  className={classes.rewardCheckmark}
                  data-testid={`${MISSIONS_TEST_ID_PREFIX}-gift-tooltip-reward-SC-claimed`}
                  src={checkmarkIcon}
                />
              )}
            </div>
          )}
          {gcCoin > 0 && (
            <div className={classes.reward}>
              <CoinWithValue
                isPile
                className={classNames(classes.tooltipReward, { [classes.rewardDimmed]: claimed })}
                coinSide="top"
                coinType="GC"
                coinWidth={40}
                dataTestId={`${MISSIONS_TEST_ID_PREFIX}-gift-tooltip-reward-GC`}
                valueClassName={classes.coinValue}
                valueDataTestId={`${MISSIONS_TEST_ID_PREFIX}-gift-tooltip-reward-GC-value`}
              >
                <span className={classes.coinValue}>{formatValueWithK(gcCoin, t)}</span>
              </CoinWithValue>
              {claimed && (
                <img
                  alt=""
                  className={classes.rewardCheckmark}
                  data-testid={`${MISSIONS_TEST_ID_PREFIX}-gift-tooltip-reward-GC-claimed`}
                  src={checkmarkIcon}
                />
              )}
            </div>
          )}
        </div>
      </DynamicTooltip>
    </>
  );
};
